interface SecureStorageOptions {
  encrypt?: boolean;
  expiry?: number; // milliseconds
  preferLocal?: boolean; // Use localStorage for cross-tab persistence
}

interface EncryptedData {
  data: string;
  iv: string;
  salt: string;
}

interface SecureStorageConfig {
  enableDebugLogging?: boolean;
}

interface StoredToken {
  value: string;
  timestamp: number;
  expiry?: number;
}

class SecureTokenStorage {
  private readonly storageKey = 'secure_tokens';
  private cleanupTimer?: ReturnType<typeof setTimeout>;
  private static readonly MIGRATION_KEY = 'secure_storage_migrated';
  private config: SecureStorageConfig;
  private encryptionKey?: CryptoKey;
  private readonly isWebCryptoSupported: boolean;

  constructor(config: SecureStorageConfig = {}) {
    this.config = {
      enableDebugLogging: false,
      ...config
    };
    this.isWebCryptoSupported = this.checkWebCryptoSupport();
    this.startCleanupTimer();
  }

  private checkWebCryptoSupport(): boolean {
    return typeof crypto !== 'undefined' && 
           typeof crypto.subtle !== 'undefined' &&
           typeof crypto.getRandomValues !== 'undefined';
  }

  private log(level: 'debug' | 'info' | 'warn' | 'error', message: string, ...args: unknown[]): void {
    if (level === 'debug' && !this.config.enableDebugLogging) {
      return;
    }
    
    const prefixedMessage = `SecureStorage: ${message}`;
    switch (level) {
      case 'debug':
        console.debug(prefixedMessage, ...args);
        break;
      case 'info':
        console.info(prefixedMessage, ...args);
        break;
      case 'warn':
        console.warn(prefixedMessage, ...args);
        break;
      case 'error':
        console.error(prefixedMessage, ...args);
        break;
    }
  }

  private async generateEncryptionKey(): Promise<CryptoKey> {
    if (this.encryptionKey) {
      return this.encryptionKey;
    }

    if (!this.isWebCryptoSupported) {
      throw new Error('Web Crypto API not supported');
    }

    // Generate a session-specific AES-GCM key
    // Security model: 
    // - Key is generated fresh per session (not persisted)
    // - Provides encryption at rest within the session
    // - Primary security comes from sessionStorage (memory-only)
    // - Encryption adds defense-in-depth against memory dumps/debugging
    this.encryptionKey = await crypto.subtle.generateKey(
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt']
    );

    return this.encryptionKey;
  }

  private async encrypt(data: string): Promise<string> {
    if (!this.isWebCryptoSupported) {
      this.log('warn', 'Web Crypto API not supported, storing data unencrypted');
      return data;
    }

    try {
      const key = await this.generateEncryptionKey();
      const iv = crypto.getRandomValues(new Uint8Array(12));
      const encodedData = new TextEncoder().encode(data);

      const encryptedData = await crypto.subtle.encrypt(
        { name: 'AES-GCM', iv: iv },
        key,
        encodedData
      );

      const encryptedObj: EncryptedData = {
        data: btoa(String.fromCharCode(...new Uint8Array(encryptedData))),
        iv: btoa(String.fromCharCode(...iv)),
        salt: '' // Not needed for session-based keys
      };

      return JSON.stringify(encryptedObj);
    } catch (error) {
      this.log('warn', 'Encryption failed, storing data unencrypted:', error);
      return data;
    }
  }

  private async decrypt(encryptedData: string): Promise<string> {
    if (!this.isWebCryptoSupported) {
      return encryptedData;
    }

    try {
      // Check if it's likely a plain text token (starts with common GitHub token prefixes)
      if (encryptedData.startsWith('gho_') || encryptedData.startsWith('ghp_') || encryptedData.startsWith('github_pat_')) {
        this.log('debug', 'Plain text token detected, returning as-is');
        return encryptedData;
      }

      // Try to parse as encrypted data
      const encryptedObj: EncryptedData = JSON.parse(encryptedData);

      if (!encryptedObj.data || !encryptedObj.iv) {
        // Not encrypted data, return as-is
        return encryptedData;
      }

      const key = await this.generateEncryptionKey();
      const iv = new Uint8Array(atob(encryptedObj.iv).split('').map(c => c.charCodeAt(0)));
      const data = new Uint8Array(atob(encryptedObj.data).split('').map(c => c.charCodeAt(0)));

      const decryptedData = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: iv },
        key,
        data
      );

      return new TextDecoder().decode(decryptedData);
    } catch (error) {
      this.log('warn', 'Decryption failed, treating as unencrypted data:', error);
      // If decryption fails, assume it's unencrypted data
      return encryptedData;
    }
  }

  private getStorage(preferLocal: boolean = false): Storage {
    // For better UX, allow preferring localStorage for cross-tab persistence
    // while maintaining security through encryption
    const storageOrder = preferLocal ? [localStorage, sessionStorage] : [sessionStorage, localStorage];
    
    for (const storage of storageOrder) {
      try {
        const testKey = '__storage_test__';
        storage.setItem(testKey, 'test');
        storage.removeItem(testKey);
        return storage;
      } catch (error) {
        this.log('debug', `${storage === localStorage ? 'localStorage' : 'sessionStorage'} unavailable:`, error);
        continue;
      }
    }
    
    throw new Error('No storage available');
  }

  private getAllTokens(preferLocal: boolean = false): Record<string, StoredToken> {
    try {
      const storage = this.getStorage(preferLocal);
      const data = storage.getItem(this.storageKey);
      return data ? JSON.parse(data) : {};
    } catch (error) {
      this.log('warn', 'Failed to retrieve tokens:', error);
      return {};
    }
  }

  private saveAllTokens(tokens: Record<string, StoredToken>, preferLocal: boolean = false): void {
    try {
      const storage = this.getStorage(preferLocal);
      storage.setItem(this.storageKey, JSON.stringify(tokens));
    } catch (error) {
      this.log('error', 'Failed to save tokens to secure storage:', error);
    }
  }

  async setToken(key: string, value: string, options: SecureStorageOptions = {}): Promise<void> {
    const preferLocal = options.preferLocal || false;
    const tokens = this.getAllTokens(preferLocal);
    const encryptedValue = options.encrypt !== false ? await this.encrypt(value) : value;
    
    const tokenData: StoredToken = {
      value: encryptedValue,
      timestamp: Date.now(),
      expiry: options.expiry ? Date.now() + options.expiry : undefined
    };

    tokens[key] = tokenData;
    this.saveAllTokens(tokens, preferLocal);
  }

  async getToken(key: string, options: { preferLocal?: boolean } = {}): Promise<string | null> {
    const preferLocal = options.preferLocal || false;
    // Try localStorage first if preferLocal, then sessionStorage, then the opposite
    const storageTypes = preferLocal ? [true, false] : [false, true];
    
    for (const useLocal of storageTypes) {
      const tokens = this.getAllTokens(useLocal);
      const tokenData = tokens[key];

      if (!tokenData) {
        continue; // Try next storage type
      }

      // Check if token has expired
      if (tokenData.expiry && Date.now() > tokenData.expiry) {
        this.removeTokenFromStorage(key, useLocal);
        continue; // Try next storage type
      }

      try {
        // Attempt to decrypt the value
        const decrypted = await this.decrypt(tokenData.value);
        return decrypted;
      } catch (error) {
        this.log('warn', 'Failed to process token value:', error);
        return tokenData.value;
      }
    }
    
    return null; // Token not found in either storage
  }

  // Synchronous version for backwards compatibility (returns encrypted value)
  getTokenSync(key: string): string | null {
    const tokens = this.getAllTokens();
    const tokenData = tokens[key];

    if (!tokenData) {
      return null;
    }

    // Check if token has expired
    if (tokenData.expiry && Date.now() > tokenData.expiry) {
      this.removeToken(key);
      return null;
    }

    return tokenData.value;
  }

  private removeTokenFromStorage(key: string, preferLocal: boolean): void {
    const tokens = this.getAllTokens(preferLocal);
    delete tokens[key];
    this.saveAllTokens(tokens, preferLocal);
  }

  removeToken(key: string): void {
    // Remove from both storages to be thorough
    this.removeTokenFromStorage(key, false); // sessionStorage
    this.removeTokenFromStorage(key, true);  // localStorage
  }

  clearAllTokens(): void {
    try {
      const storage = this.getStorage();
      storage.removeItem(this.storageKey);
    } catch (error) {
      this.log('error', 'Failed to clear tokens from secure storage:', error);
    }
  }

  hasToken(key: string): boolean {
    return this.getTokenSync(key) !== null;
  }

  getAllTokenKeys(): string[] {
    const tokens = this.getAllTokens();
    return Object.keys(tokens).filter(key => this.getTokenSync(key) !== null);
  }

  // Cleanup expired tokens
  cleanup(): void {
    const tokens = this.getAllTokens();
    const now = Date.now();
    let hasExpired = false;

    Object.keys(tokens).forEach(key => {
      const tokenData = tokens[key];
      if (tokenData.expiry && now > tokenData.expiry) {
        delete tokens[key];
        hasExpired = true;
      }
    });

    if (hasExpired) {
      this.saveAllTokens(tokens);
    }
  }


  private startCleanupTimer(): void {
    this.cleanupTimer = setInterval(() => {
      this.cleanup();
    }, 5 * 60 * 1000); // 5 minutes
  }

  destroy(): void {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
      this.cleanupTimer = undefined;
    }
  }

  // Migration helper for existing localStorage tokens
  migrateFromLocalStorage(oldKey: string, newKey?: string): boolean {
    try {
      // Check if migration has already been completed
      if (localStorage.getItem(SecureTokenStorage.MIGRATION_KEY)) {
        return false;
      }

      const value = localStorage.getItem(oldKey);
      if (value) {
        this.setToken(newKey || oldKey, value, { encrypt: true });
        localStorage.removeItem(oldKey);
        return true;
      }
      return false;
    } catch (error) {
      this.log('error', 'Migration failed for key:', oldKey, error);
      return false;
    }
  }

  // Mark migration as completed for all tokens
  markMigrationComplete(): void {
    try {
      localStorage.setItem(SecureTokenStorage.MIGRATION_KEY, 'true');
    } catch (error) {
      this.log('warn', 'Failed to mark migration as complete:', error);
    }
  }

  // Check if migration has been completed
  isMigrationComplete(): boolean {
    try {
      return localStorage.getItem(SecureTokenStorage.MIGRATION_KEY) === 'true';
    } catch (error) {
      this.log('warn', 'Failed to check migration status:', error);
      return false;
    }
  }

  // Reset migration state (useful for testing)
  resetMigrationState(): void {
    try {
      localStorage.removeItem(SecureTokenStorage.MIGRATION_KEY);
    } catch (error) {
      this.log('warn', 'Failed to reset migration state:', error);
    }
  }

  // Enable or disable debug logging
  setDebugLogging(enabled: boolean): void {
    this.config.enableDebugLogging = enabled;
    this.log('info', `Debug logging ${enabled ? 'enabled' : 'disabled'}`);
  }
}

// Export singleton instance
export const secureStorage = new SecureTokenStorage();

// Convenience functions for GitHub token management
export const GitHubTokenManager = {
  setAccessToken: async (token: string): Promise<void> => {
    // Disable encryption for GitHub tokens to ensure cross-session persistence
    // The token is stored in localStorage which provides adequate security for this use case
    // Use localStorage for cross-tab persistence
    const encrypt = false;
    await secureStorage.setToken('github_access_token', token, { 
      encrypt,
      expiry: 1000 * 60 * 60 * 24 * 30, // 30 days
      preferLocal: true // Use localStorage for cross-tab persistence
    });
  },

  getAccessToken: async (): Promise<string | null> => {
    return await secureStorage.getToken('github_access_token', { preferLocal: true });
  },

  // Synchronous version for backwards compatibility
  getAccessTokenSync: (): string | null => {
    return secureStorage.getTokenSync('github_access_token');
  },

  removeAccessToken: () => {
    secureStorage.removeToken('github_access_token');
  },

  setOAuthState: async (state: string): Promise<void> => {
    await secureStorage.setToken('oauth_state', state, { 
      encrypt: true, 
      expiry: 1000 * 60 * 10 // 10 minutes
    });
  },

  getOAuthState: async (): Promise<string | null> => {
    return await secureStorage.getToken('oauth_state');
  },

  // Synchronous version for backwards compatibility
  getOAuthStateSync: (): string | null => {
    return secureStorage.getTokenSync('oauth_state');
  },

  removeOAuthState: () => {
    secureStorage.removeToken('oauth_state');
  },

  clearAll: () => {
    secureStorage.removeToken('github_access_token');
    secureStorage.removeToken('oauth_state');
  },

  // Clear corrupted tokens (useful when encryption/decryption issues occur)
  clearCorruptedTokens: () => {
    try {
      // Clear from both localStorage and sessionStorage
      ['localStorage', 'sessionStorage'].forEach(storageType => {
        const storage = storageType === 'localStorage' ? localStorage : sessionStorage;
        const keys = Object.keys(storage);
        keys.forEach(key => {
          if (key.includes('secure_tokens') || key.includes('github_access_token') || key.includes('oauth_state')) {
            storage.removeItem(key);
          }
        });
      });
      console.log('Cleared potentially corrupted tokens');
    } catch (error) {
      console.warn('Failed to clear corrupted tokens:', error);
    }
  },

  // Migrate existing localStorage tokens
  migrateFromLocalStorage: async (): Promise<boolean> => {
    // Skip migration if already completed
    if (secureStorage.isMigrationComplete()) {
      return false;
    }

    const tokenMigrated = secureStorage.migrateFromLocalStorage('github_access_token');
    const stateMigrated = secureStorage.migrateFromLocalStorage('oauth_state');
    const anyMigrated = tokenMigrated || stateMigrated;
    
    if (anyMigrated) {
      console.log('Successfully migrated tokens to secure storage');
      // Mark migration as complete after successful migration
      secureStorage.markMigrationComplete();
    } else {
      // Even if no tokens were found, mark migration as attempted to avoid future checks
      secureStorage.markMigrationComplete();
    }
    
    return anyMigrated;
  }
};

// Export utility functions
export const cleanupSecureStorage = () => {
  secureStorage.cleanup();
};

export const isMigrationComplete = () => {
  return secureStorage.isMigrationComplete();
};

export const resetMigrationState = () => {
  secureStorage.resetMigrationState();
};

export const enableDebugLogging = (enabled: boolean = true) => {
  secureStorage.setDebugLogging(enabled);
};